import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadCommunicationOptOutTypes,
    loadContractDetailsForContractId,
    loadDataProtectionDetails,
    loadGenderDetails,
    loadParticipantStatusDetails,
    loadParticipantStatusReasonDetails,
    loadPreferredPronounDetails,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import {
    clearBusinessLogicError,
    clearTimelineEventsSearchData,
    clearValidationErrors,
    loadContractConstants,
    loadPrimaryDetailsAuditEvent,
    searchEligibilityGroups,
    updateParticipant
} from '../../../store/participantService';
import { selectAdviserTypeIds } from '../../../store/userSelectors';
import {
    clearTeamsForService,
    clearUsersBySearch,
    loadTeamsForService,
    loadUserById,
    loadUsersBySearch,
    loadUserTypes
} from '../../../store/userService';
import { getConfiguredItems, getNameFromId, getObjFromId } from '../../../utils/directusFunctions';
import {
    ADDRESS_LINE_1_MAX_LENGTH,
    ADDRESS_LINE_2_MAX_LENGTH,
    ADDRESS_LINE_3_MAX_LENGTH,
    CITY_MAX_LENGTH,
    MAX_EMAIL,
    MAX_NAME,
    MAX_PHONE,
    MAX_POSTCODE,
    MAX_PRONOUN
} from '../../../utils/formValidation/commonConstants';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    COMMUNICATION_OPT_OUT_TYPES_ERROR,
    CONTRACT_DETAILS_ERROR,
    ELIGIBILITY_GROUPS_ERROR,
    GENDER_ERROR,
    NO_ADVISERS_ERROR,
    PARTICIPANT_STATUS_DETAILS_ERROR,
    PARTICIPANT_STATUS_REASONS_ERROR,
    SERVICES_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { normalizePhoneNumber } from '../../../utils/formValidation/validationFunctions';
import { addFullNameToArray } from '../../../utils/userArrayUtils';
import {
    ADVISER,
    hasRole,
    LOCAL_ADMIN,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import AuditEventHistoryModal from '../../auditing/auditTimelineEventHistory/AuditTimelineEventHistoryModal';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import MultiSelect from '../../formElements/MultiSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import Switch from '../../formElements/Switch';
import TextInputField from '../../formElements/TextInputField';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ScreenError from '../../ui/messageBanners/ScreenError';

import { validate } from './validatePrimaryDetails';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const _UPDATE_MESSAGE = 'Participant primary details have been updated';
const acceptedRoles = [ADVISER, MANAGER, PRAP, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const canUpdateRoles = [
    ADVISER,
    MANAGER,
    LOCAL_ADMIN,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
];
const uniqueIdRoles = [LOCAL_ADMIN, QUALITY, SUPERUSER];
const changeTeamRoles = [ADVISER, MANAGER, LOCAL_ADMIN, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const qualityAndUpRoles = [QUALITY, SUPERUSER];
const prapAndUpRoles = [PRAP, QUALITY, SUPERUSER];

const PrimaryDetails = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: null,
        firstName: '',
        middleName: '',
        lastName: '',
        preferredName: '',
        preferredPronounsId: '',
        preferredPronouns: '',
        genderId: '',
        userId: '',
        statusId: '',
        statusReasonId: '',
        dateOfBirth: '',
        uniqueIdentifier: '',
        repeatUniqueIdentifier: '',
        address1: '',
        address2: '',
        address3: '',
        city: '',
        postcode: '',
        primaryPhoneNumber: '',
        secondaryPhoneNumber: '',
        emailAddress: '',
        preferredMethodOfContact: '',
        communicationTypeOptOutIds: [],
        careOfAddress: 'No',
        startDate: '',
        eligibilityGroupId: '',
        referralDate: '',
        goodNewsStory: 'No',
        contractId: '',
        serviceId: '',
        teamId: '',
        uniqueIdentifierType: '',
        orionMembershipId: '',
        dataProtectionId: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [arrayGenders, setArrayGenders] = useState([]);
    const [arrayAdvisers, setArrayAdvisers] = useState([]);
    const [advisers, setAdvisers] = useState([]);
    const [isExistingAdviserExpired, setIsExistingAdviserExpired] = useState(false);
    const [eligibilityGroups, setEligibilityGroups] = useState([]);
    const [statusDetails, setStatusDetails] = useState([]);
    const [statusReasons, setStatusReasons] = useState([]);
    const [services, setServices] = useState([]);
    const [teams, setTeams] = useState([]);
    const [isTeamsLoaded, setisTeamsLoaded] = useState(false);
    const [displayPreferredPronounsOther, setDisplayPreferredPronounsOther] = useState(true);
    const [preSelectedCommunicationOptOutTypeIds, setPreSelectedCommunicationOptOutTypeIds] =
        useState([]);
    const [preSelectedCommunicationOptOutTypes, setPreSelectedCommunicationOptOutTypes] = useState(
        []
    );
    const [participantContractDetails, setParticipantContractDetails] = useState({});
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [disableStatusReason, setDisableStatusReason] = useState(false);
    const [adjustedRoles, setAdjustedRoles] = useState([]);

    const [isDisabled, setIsDisabled] = useState(true);
    const [isDisabledUniqueId, setIsDisabledUniqueId] = useState(true);
    const [isDisabledTeam, setIsDisabledTeam] = useState(true);
    const [isDisabledReferralDate, setIsDisabledReferralDate] = useState(true);
    const [isDisabledUpdate, setIsDisabledUpdate] = useState(true);
    const [isDisabledStatus, setIsDisabledStatus] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const eligibilityGroupsForContractId = useSelector(
        (state) => state.entities.participantService.eligibilityGroupSearch
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const userSearch = useSelector((state) => state.entities.userService.usersBySearch);
    const userSearchMetaData = useSelector(
        (state) => state.entities.userService.usersBySearchMetaData
    );
    const usersById = useSelector((state) => state.entities.userService.usersById);
    const userTypes = useSelector((state) => state.entities.userService.userTypes);
    const adviserTypeIds = useSelector(selectAdviserTypeIds);
    const teamsForService = useSelector((state) => state.entities.userService.teamsForService);
    const loadingTeamsForService = useSelector(
        (state) => state.entities.userService.loadingTeamsForService
    );
    const communicationOptOutTypes = useSelector(
        (state) => state.entities.directusService.communicationOptOutTypes
    );
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const dataProtectionDetails = useSelector(
        (state) => state.entities.directusService.dataProtectionDetails
    );
    const genderDetails = useSelector((state) => state.entities.directusService.genderDetails);
    const participantStatusDetails = useSelector(
        (state) => state.entities.directusService.participantStatusDetails
    );
    const participantStatusReasonDetails = useSelector(
        (state) => state.entities.directusService.participantStatusReasonDetails
    );
    const preferredPronounDetails = useSelector(
        (state) => state.entities.directusService.preferredPronounDetails
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );
    const loadingParticipantTimelineSearchData = useSelector(
        (state) => state.entities.participantService.loadingParticipantTimelineEventsSearchData
    );
    const participantTimelineSearchData = useSelector(
        (state) => state.entities.participantService.participantTimelineEventsSearchData
    );
    const participantTimelineSearchDataMetaData = useSelector(
        (state) => state.entities.participantService.participantTimelineEventsSearchMetaData
    );
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const validationErrors = useSelector(
        (state) => state.entities.participantService.validationErrors
    );
    const businessLogicError = useSelector(
        (state) => state.entities.participantService.businessLogicError
    );

    const prevLoadingTeamsRef = useRef(loadingTeamsForService);

    // USE EFFECTS
    useEffect(() => {
        dispatch(clearUsersBySearch());
        dispatch(clearTeamsForService());
        setEligibilityGroups([]);
        setServices([]);
        userTypes?.length < 1 && dispatch(loadUserTypes());
        communicationOptOutTypes?.length < 1 && dispatch(loadCommunicationOptOutTypes());
        genderDetails?.length < 1 && dispatch(loadGenderDetails());
        dataProtectionDetails?.length < 1 && dispatch(loadDataProtectionDetails());
        participantStatusDetails?.length < 1 && dispatch(loadParticipantStatusDetails());
        participantStatusReasonDetails?.length < 1 &&
            dispatch(loadParticipantStatusReasonDetails());
        preferredPronounDetails?.length < 1 && dispatch(loadPreferredPronounDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!('id' in currentParticipant)) return;
        updateInitialEntry();
    }, [currentParticipant]);

    useEffect(() => {
        if (!('id' in currentParticipant)) return;
        const isDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(qualityAndUpRoles, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || isDisabledOnClosedStatus);
        setIsDisabledUniqueId(!hasRole(uniqueIdRoles, roles) || isDisabledOnClosedStatus);
        setIsDisabledTeam(!hasRole(changeTeamRoles, roles) || isDisabledOnClosedStatus);
        setIsDisabledReferralDate(!hasRole(prapAndUpRoles, roles) || isDisabledOnClosedStatus);
        setIsDisabledUpdate(!hasRole(canUpdateRoles, roles));
        setIsDisabledStatus(!hasRole(acceptedRoles, roles));
    }, [currentParticipant.id, currentParticipant.status, roles]);

    useEffect(() => {
        setAdjustedRoles(
            roles.includes(RECRUITMENT_MANAGER) && !roles.includes(ADVISER)
                ? [...roles, ADVISER]
                : roles
        );
    }, [roles]);

    useEffect(() => {
        setAdvisers(userSearch);
    }, [userSearch]);

    useEffect(() => {
        setErrors({});
        if (!currentParticipant?.id) return;
        if (currentParticipant.userId) {
            const initialAdviser = usersById[currentParticipant.userId];
            initialAdviser
                ? setArrayAdvisers(addFullNameToArray([initialAdviser]))
                : dispatch(loadUserById(currentParticipant.userId));
        }
        dispatch(loadContractConstants(currentParticipant.contractId));
        dispatch(searchEligibilityGroups({ contractId: currentParticipant.contractId }));
        if (!(currentParticipant.contractId in contractDetailsById))
            dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
        if (!(currentParticipant.contractId in servicesByContractId))
            dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
    }, [currentParticipant.id, currentParticipant?.userId]);

    useEffect(() => {
        if (!currentParticipant?.teamId || !adviserTypeIds.length) return;
        dispatch(
            loadUsersBySearch(
                {
                    teamIds: [currentParticipant?.teamId],
                    active: true,
                    userTypeIds: adviserTypeIds
                },
                0
            )
        );
    }, [currentParticipant.teamId, adviserTypeIds]);

    useEffect(() => {
        if (!newEntry.serviceId) return;
        dispatch(loadTeamsForService({ serviceIds: [newEntry.serviceId] }));

        if (newEntry.serviceId === initialStateOnEntry.serviceId)
            setNewEntry((prev) => ({ ...prev, teamId: initialStateOnEntry.teamId || '' }));
        else setNewEntry((prev) => ({ ...prev, teamId: '' }));
    }, [newEntry.serviceId]);

    useEffect(() => {
        if (prevLoadingTeamsRef.current && !loadingTeamsForService) {
            setTeams(
                teamsForService.filter(
                    (el) =>
                        loggedInUser.teamIds.includes(el.id) || el.id === initialStateOnEntry.teamId
                )
            );
            setisTeamsLoaded(true);
        }
        prevLoadingTeamsRef.current = loadingTeamsForService;
    }, [loadingTeamsForService]);

    useEffect(() => {
        if (!isTeamsLoaded) return;
        const teamIds = teams?.map((el) => el.id) || [];
        if (!teamIds.includes(newEntry.teamId)) setNewEntry((prev) => ({ ...prev, teamId: '' }));
    }, [teams]);

    useEffect(() => {
        if (currentParticipant?.userId && usersById[currentParticipant.userId]) {
            setArrayAdvisers(addFullNameToArray([usersById[currentParticipant.userId]]));
            const advisersTeamIds = [
                ...usersById[currentParticipant.userId].otherTeams,
                usersById[currentParticipant.userId].primaryTeam
            ]
                .filter((el) => el)
                .map((el) => el.id);
            setIsExistingAdviserExpired(!advisersTeamIds.includes(currentParticipant.teamId));
        }
    }, [usersById, currentParticipant.userId, currentParticipant.teamId]);

    useEffect(() => {
        if (
            !participantStatusDetails.length ||
            !contractConstants.participantStatuses ||
            contractConstants.participantStatuses.length === 0
        )
            return;

        const createdStatus = participantStatusDetails.find((el) => el.name === 'Created');
        const tempStatusId = currentParticipant.statusId || createdStatus.id;
        const currentStatus = contractConstants.participantStatuses.find(
            (el) => el.participantStatusId === tempStatusId
        );

        if (!currentStatus) {
            setStatusDetails([createdStatus]);
            updateInitialEntry({ ...currentParticipant, statusId: createdStatus.id });
            return;
        }

        const activeStatusIds = currentStatus.toParticipantStatuses
            .filter((el) => {
                if (currentStatus.participantStatusId === el.participantStatusId) return false;
                const status = contractConstants.participantStatuses.find(
                    (entry) => entry.participantStatusId === el.participantStatusId
                );

                if (
                    el.participantStatusReasons.some((el) =>
                        hasRole([el.selectableBy.toUpperCase(), SUPERUSER], adjustedRoles)
                    )
                )
                    return status.active;
                else return false;
            })
            .map((el) => el.participantStatusId);

        const newStatusDetails = activeStatusIds.map((el) =>
            participantStatusDetails.find((entry) => entry.id === el)
        );
        const currentStatusDetails = participantStatusDetails.find(
            (el) => el.id === currentStatus.participantStatusId
        );
        setStatusDetails([...newStatusDetails, currentStatusDetails]);

        updateInitialEntry({ ...currentParticipant, statusId: currentStatus.participantStatusId });
    }, [contractConstants, participantStatusDetails, adjustedRoles, initialStateOnEntry.statusId]);

    useEffect(() => {
        if (initialStateOnEntry.statusId.length === 0) return;
        if (
            newEntry.statusId === initialStateOnEntry.statusId &&
            !contractConstants.participantStatuses
                ?.find((el) => el.participantStatusId === initialStateOnEntry.statusId)
                ?.toParticipantStatuses.some(
                    (el) => el.participantStatusId === initialStateOnEntry.statusId
                )
        ) {
            setDisableStatusReason(true);
            const staticReason = participantStatusReasonDetails?.find(
                (el) => el.id === initialStateOnEntry.statusReasonId
            );
            setNewEntry((prev) => ({
                ...prev,
                statusReasonId: initialStateOnEntry.statusReasonId
            }));
            setStatusReasons(staticReason ? [staticReason] : []);
            return;
        }
        if (!contractConstants.participantStatuses) return;
        const existingStatus = contractConstants.participantStatuses.find(
            (el) => el.participantStatusId === initialStateOnEntry.statusId
        );
        const newStatus = existingStatus?.toParticipantStatuses.find(
            (el) => el.participantStatusId === newEntry.statusId
        );
        if (!newStatus) return;

        const newStatusReasons = participantStatusReasonDetails.filter(
            (el) =>
                newStatus.participantStatusReasons.some(
                    (entry) =>
                        el.id === entry.participantStatusReasonId &&
                        hasRole([entry.selectableBy.toUpperCase(), SUPERUSER], adjustedRoles)
                ) ||
                (newEntry.statusId === initialStateOnEntry.statusId &&
                    el.id === initialStateOnEntry.statusReasonId)
        );
        setDisableStatusReason(
            (newEntry.statusReasonId &&
                newEntry.statusId === initialStateOnEntry.statusId &&
                newStatusReasons.length === 1) ||
                (!newEntry.statusReasonId && newStatusReasons.length === 0)
        );
        setStatusReasons(newStatusReasons);
        const currentStatusReason = newStatusReasons?.find(
            (el) => el.id === initialStateOnEntry.statusReasonId
        );
        setNewEntry((prev) => ({
            ...prev,
            statusReasonId: currentStatusReason ? currentStatusReason.id : ''
        }));
    }, [
        statusDetails,
        newEntry.statusId,
        initialStateOnEntry.statusId,
        contractConstants,
        participantStatusReasonDetails
    ]);

    useEffect(() => {
        if (genderDetails?.length > 0 && currentParticipant?.contractId) {
            setArrayGenders(getConfiguredItems(genderDetails, currentParticipant.contractId));
        }
    }, [genderDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (!eligibilityGroupsForContractId || eligibilityGroupsForContractId?.length < 1) return;
        setEligibilityGroups(eligibilityGroupsForContractId);
    }, [eligibilityGroupsForContractId]);

    useEffect(() => {
        if (communicationOptOutTypes?.length && newEntry?.communicationTypeOptOutIds) {
            const preSelects = communicationOptOutTypes.filter((el) =>
                newEntry.communicationTypeOptOutIds.includes(el.id)
            );
            setPreSelectedCommunicationOptOutTypes(preSelects);
            setPreSelectedCommunicationOptOutTypeIds(preSelects.map((el) => el.id));
        }
    }, [communicationOptOutTypes]);

    useEffect(() => {
        if (!(currentParticipant.contractId in contractDetailsById)) return;
        setParticipantContractDetails(contractDetailsById[currentParticipant.contractId]);
    }, [contractDetailsById, currentParticipant.contractId]);

    useEffect(() => {
        if (!(currentParticipant.contractId in servicesByContractId)) return;
        setServices(
            servicesByContractId[currentParticipant.contractId].filter(
                (el) =>
                    el.status !== 'archived' &&
                    (loggedInUser.serviceIds.includes(el.id) ||
                        initialStateOnEntry.serviceId === el.id)
            )
        );
    }, [servicesByContractId, currentParticipant.contractId, initialStateOnEntry.serviceId]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (advisers.length < 1) return;
        let updatedAdvisers = advisers;
        if (newEntry.userId && !updatedAdvisers.some((el) => el.id === newEntry.userId)) {
            // Put selected adviser at the top of dropdown if it's not in the updated advisers array
            const selectedAdviser = getObjFromId(arrayAdvisers, newEntry.userId);
            if (Object.keys(selectedAdviser).length > 0)
                updatedAdvisers = [selectedAdviser, ...updatedAdvisers];
        }
        setArrayAdvisers(addFullNameToArray(updatedAdvisers));
    }, [advisers]);

    useEffect(() => {
        if (currentParticipant.teamId) {
            dispatch(clearUsersBySearch());
        }
    }, [currentParticipant.teamId]);

    useEffect(() => {
        if (!isAuditable || !auditLabel) return;
        if (
            participantTimelineSearchData?.length > 0 &&
            !loadingParticipantTimelineSearchData &&
            'number' in participantTimelineSearchDataMetaData &&
            !participantTimelineSearchDataMetaData?.last
        ) {
            dispatch(
                loadPrimaryDetailsAuditEvent(
                    {
                        participantIds: [newEntry.id],
                        field: auditLabel
                    },
                    participantTimelineSearchDataMetaData.number + 1
                )
            );
        } else if (participantTimelineSearchDataMetaData?.last) setShowAuditModal(true);
    }, [participantTimelineSearchDataMetaData, participantTimelineSearchData, auditLabel]);

    useEffect(() => {
        if (successMessage === _UPDATE_MESSAGE) {
            setInitialStateOnEntry(newEntry);
            updateInitialEntry();
        }
        isButtonDisabled && setIsButtonDisabled(false);
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
        if (businessLogicError && Object.keys(businessLogicError).length > 0) {
            setErrors(businessLogicError);
            dispatch(clearBusinessLogicError());
        }
    }, [successMessage, errorMessage, validationErrors, businessLogicError]);

    // HELPER FNS
    const loadingError = () => {
        if (genderDetails?.length < 1) return GENDER_ERROR;
        if (participantStatusDetails?.length < 1) return PARTICIPANT_STATUS_DETAILS_ERROR;
        if (participantStatusReasonDetails?.length < 1) return PARTICIPANT_STATUS_REASONS_ERROR;
        if (eligibilityGroupsForContractId?.length < 1) return ELIGIBILITY_GROUPS_ERROR;
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
        if (arrayGenders?.length < 1) return 'No genders available for this contract.';
        if (communicationOptOutTypes?.length < 1) return COMMUNICATION_OPT_OUT_TYPES_ERROR;
        if (arrayAdvisers.length < 1) return NO_ADVISERS_ERROR;
        if (Object.keys(participantContractDetails)?.length < 1) return CONTRACT_DETAILS_ERROR;
        if (services?.length < 1) return SERVICES_ERROR;
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const updateInitialEntry = (currentEntry = currentParticipant) => {
        setNewEntry({
            ...currentEntry,
            repeatUniqueIdentifier: currentEntry.uniqueIdentifier
        });
        setInitialStateOnEntry({
            ...currentEntry,
            repeatUniqueIdentifier: currentEntry.uniqueIdentifier
        });
    };

    const configUniqueIdLabel = (repeat = '') => {
        return currentParticipant.uniqueIdentifierType === 'NINO'
            ? `NINO ${repeat} Unique ID`
            : currentParticipant.uniqueIdentifierType === 'NHS_NUMBER'
              ? `NHS Number ${repeat} Unique ID`
              : `${repeat} Unique ID`;
    };

    const cleanUp = () => {
        dispatch(clearTimelineEventsSearchData());
    };

    // EVENT HANDLERS
    const onSwitchAuditDisplayFields = (e) => {
        setIsAuditable(e.target.checked);
    };

    const onUpdate = (key, value) => {
        clearError(key);
        if (value == null) value = '';
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateAdviser = (chosenId) => {
        if (isExistingAdviserExpired) {
            const existingAdviserId =
                arrayAdvisers.find((el) => el.id === currentParticipant.userId)?.id || '';
            if (existingAdviserId) {
                setArrayAdvisers((prev) => prev.filter((el) => el.id !== existingAdviserId));
                setIsExistingAdviserExpired(false);
            }
        }
        onUpdate('userId', chosenId);
    };

    const onUpdatePreferredPronounsId = (chosenId) => {
        const name = getNameFromId(preferredPronounDetails, chosenId);
        setDisplayPreferredPronounsOther(name === 'Other');
        setNewEntry((prev) => ({
            ...prev,
            preferredPronounsId: chosenId,
            preferredPronouns: name !== 'Other' ? '' : newEntry.preferredPronouns
        }));
    };

    const onUpdateUniqueIdentifier = (key, value) => {
        clearError(key);
        if (value == null) value = '';
        const additionalChanges = {
            uniqueIdentifierType:
                value === 'TEMPORARY' &&
                (newEntry.uniqueIdentifier === 'TEMPORARY' ||
                    newEntry.repeatUniqueIdentifier === 'TEMPORARY')
                    ? 'TEMPORARY'
                    : participantContractDetails.unique_identifier_type.name
        };
        setNewEntry((prev) => ({ ...prev, ...additionalChanges, [key]: value }));
    };

    const onLoadMoreAdvisers = () => {
        if (userSearchMetaData?.last) return;
        dispatch(
            loadUsersBySearch(
                {
                    teamIds: [currentParticipant?.teamId],
                    active: true,
                    userTypeIds: adviserTypeIds
                },
                !advisers.length ? 0 : userSearchMetaData.number + 1
            )
        );
    };

    const onSearchAuditItems = (label) => {
        dispatch(
            loadPrimaryDetailsAuditEvent(
                {
                    participantIds: [newEntry.id],
                    field: label
                },
                0
            )
        );
        setAuditLabel(label);
    };

    const onModalClose = (e, reason) => {
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearTimelineEventsSearchData());
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const normalizedEntry = {
            ...newEntry,
            primaryPhoneNumber:
                typeof newEntry.primaryPhoneNumber === 'string'
                    ? normalizePhoneNumber(newEntry.primaryPhoneNumber)
                    : newEntry.primaryPhoneNumber,
            secondaryPhoneNumber:
                typeof newEntry.secondaryPhoneNumber === 'string'
                    ? normalizePhoneNumber(newEntry.secondaryPhoneNumber)
                    : newEntry.secondaryPhoneNumber
        };
        const normalizedInitialStateOnEntry = {
            ...initialStateOnEntry,
            primaryPhoneNumber:
                typeof initialStateOnEntry.primaryPhoneNumber === 'string'
                    ? normalizePhoneNumber(initialStateOnEntry.primaryPhoneNumber)
                    : initialStateOnEntry.primaryPhoneNumber,
            secondaryPhoneNumber:
                typeof initialStateOnEntry.secondaryPhoneNumber === 'string'
                    ? normalizePhoneNumber(initialStateOnEntry.secondaryPhoneNumber)
                    : initialStateOnEntry.secondaryPhoneNumber
        };
        const newErrors = validate(normalizedEntry, normalizedInitialStateOnEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setIsButtonDisabled(true);
        dispatch(updateParticipant(normalizedEntry, _UPDATE_MESSAGE));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg)
        return (
            <div className={`${app.screenWrapper}`}>
                <LoadingSpinner content={errorMsg} />
            </div>
        );

    // RENDER
    return (
        <div className={`${app.screenWrapper} ${app.fadein}`}>
            <form onSubmit={onSubmit} data-testid="form_start" className={form.form}>
                <div className={app.sectionToolBar}>
                    <h2 className={app.sectionHeading}>General Details</h2>
                    <Switch
                        id={'auditDisplayFields'}
                        label="Show Audit Fields"
                        onOff={true}
                        customClass="auditSwitch"
                        checked={isAuditable}
                        onChange={onSwitchAuditDisplayFields}
                    />
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id={'firstName'}
                            label={'First Name'}
                            placeholder={'Enter first name'}
                            disabled={isDisabled}
                            mandatory={true}
                            maxLength={MAX_NAME}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.firstName || ''}
                            error={errors.firstName}
                            onChange={(e) => onUpdate('firstName', e.target.value)}
                        />
                        <TextInputField
                            id={'middleName'}
                            label={'Middle Name'}
                            placeholder={'Enter middle name'}
                            disabled={isDisabled}
                            maxLength={MAX_NAME}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.middleName || ''}
                            error={errors.middleName}
                            onChange={(e) => onUpdate('middleName', e.target.value)}
                        />
                        <TextInputField
                            id={'lastName'}
                            label={'Last Name'}
                            placeholder={'Enter last name'}
                            disabled={isDisabled}
                            maxLength={MAX_NAME}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.lastName || ''}
                            error={errors.lastName}
                            onChange={(e) => onUpdate('lastName', e.target.value)}
                        />
                        <TextInputField
                            id={'preferredName'}
                            label={'Preferred Name'}
                            placeholder={'Enter preferred name'}
                            disabled={isDisabled}
                            maxLength={MAX_NAME}
                            value={newEntry.preferredName || ''}
                            error={errors.preferredName}
                            onChange={(e) => onUpdate('preferredName', e.target.value)}
                        />
                        <SingleSelect
                            id={'genderId'}
                            label={'Gender'}
                            placeholder={'Select gender...'}
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={arrayGenders || []}
                            selectedId={newEntry.genderId || ''}
                            selected={getObjFromId(arrayGenders, newEntry.genderId)}
                            error={errors.genderId}
                            onChange={(chosenId) => onUpdate('genderId', chosenId)}
                        />
                        <SingleSelect
                            id={'preferredPronounsId'}
                            label={'Preferred Pronouns'}
                            placeholder="Select preferred pronoun..."
                            disabled={isDisabled}
                            menuItems={preferredPronounDetails || []}
                            selectedId={newEntry.preferredPronounsId || ''}
                            selected={getObjFromId(
                                preferredPronounDetails,
                                newEntry.preferredPronounsId
                            )}
                            onChange={(chosenId) => onUpdatePreferredPronounsId(chosenId)}
                        />
                        <div hidden={!displayPreferredPronounsOther}>
                            <TextInputField
                                id={'preferredPronouns'}
                                label={'Preferred Pronouns Other Name'}
                                placeholder={'Enter preferred pronouns name'}
                                disabled={isDisabled}
                                maxLength={MAX_PRONOUN}
                                value={newEntry.preferredPronouns || ''}
                                error={errors.preferredPronouns}
                                onChange={(e) => onUpdate('preferredPronouns', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'adviserId'}
                            label={'Adviser'}
                            placeholder="Select adviser..."
                            disabled={isDisabled}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            menuItems={arrayAdvisers || []}
                            selectedId={newEntry.userId || ''}
                            selected={getObjFromId(arrayAdvisers, newEntry.userId)}
                            onChange={(chosenId) => onUpdateAdviser(chosenId)}
                            onLoadMoreItems={onLoadMoreAdvisers}
                            moreItemsToLoad={
                                !userSearchMetaData?.totalElements ||
                                advisers.length < userSearchMetaData?.totalElements
                            }
                        />
                        <SingleSelect
                            id={'statusId'}
                            label={'Status'}
                            placeholder="Select status..."
                            disabled={isDisabledStatus}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            mandatory={true}
                            menuItems={statusDetails || []}
                            selectedId={newEntry.statusId || ''}
                            selected={getObjFromId(participantStatusDetails, newEntry.statusId)}
                            error={errors.statusId}
                            onChange={(chosenId) => onUpdate('statusId', chosenId)}
                        />
                        <SingleSelect
                            id={'statusReason'}
                            label={'Status Reason'}
                            placeholder="Select status reason..."
                            disabled={isDisabledStatus || disableStatusReason || !newEntry.statusId}
                            mandatory={
                                newEntry.statusId !== initialStateOnEntry.statusId ||
                                newEntry.statusReasonId !== initialStateOnEntry.statusReasonId
                            }
                            menuItems={statusReasons || []}
                            selectedId={newEntry.statusReasonId || ''}
                            selected={getObjFromId(
                                participantStatusReasonDetails,
                                newEntry.statusReasonId
                            )}
                            error={errors.statusReasonId}
                            onChange={(chosenId) => onUpdate('statusReasonId', chosenId || null)}
                        />

                        <DateSelect
                            id="dateOfBirth"
                            label="Date of Birth"
                            disabled={isDisabled}
                            mandatory={true}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.dateOfBirth}
                            error={errors.dateOfBirth}
                            onDateChange={(date) => onUpdate('dateOfBirth', date)}
                        />

                        <TextInputField
                            id={'uniqueId'}
                            label={configUniqueIdLabel()}
                            placeholder={'Enter unique identifier'}
                            disabled={isDisabledUniqueId}
                            mandatory={true}
                            isAuditable={isAuditable}
                            onSearchAuditItems={() => onSearchAuditItems('Unique Identifier')}
                            value={newEntry.uniqueIdentifier || ''}
                            error={errors.uniqueIdentifier}
                            onPaste={(e) => e.preventDefault()}
                            onChange={(e) =>
                                onUpdateUniqueIdentifier('uniqueIdentifier', e.target.value)
                            }
                        />
                        <TextInputField
                            id={'repeatUniqueId'}
                            label={configUniqueIdLabel('Repeat')}
                            placeholder={'Repeat unique identifier'}
                            disabled={isDisabledUniqueId}
                            mandatory={true}
                            value={newEntry.repeatUniqueIdentifier || ''}
                            error={errors.repeatUniqueIdentifier}
                            onPaste={(e) => e.preventDefault()}
                            onChange={(e) =>
                                onUpdateUniqueIdentifier('repeatUniqueIdentifier', e.target.value)
                            }
                        />
                    </div>
                </div>

                <h2 className={app.sectionHeading}>Contact Details</h2>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id={'address1'}
                            label={'Address Line 1'}
                            placeholder={'Enter address line 1'}
                            disabled={isDisabled}
                            maxLength={ADDRESS_LINE_1_MAX_LENGTH}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.address1 || ''}
                            error={errors.address1}
                            onChange={(e) => onUpdate('address1', e.target.value)}
                        />
                        <TextInputField
                            id={'address2'}
                            label={'Address Line 2'}
                            placeholder={'Enter address line 2'}
                            disabled={isDisabled}
                            maxLength={ADDRESS_LINE_2_MAX_LENGTH}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.address2 || ''}
                            error={errors.address2}
                            onChange={(e) => onUpdate('address2', e.target.value)}
                        />
                        <TextInputField
                            id={'address3'}
                            label={'Address Line 3'}
                            placeholder={'Enter address line 3'}
                            disabled={isDisabled}
                            maxLength={ADDRESS_LINE_3_MAX_LENGTH}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.address3 || ''}
                            error={errors.address3}
                            onChange={(e) => onUpdate('address3', e.target.value)}
                        />
                        <TextInputField
                            id={'city'}
                            label={'City'}
                            placeholder={'Enter city'}
                            disabled={isDisabled}
                            maxLength={CITY_MAX_LENGTH}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.city || ''}
                            error={errors.city}
                            onChange={(e) => onUpdate('city', e.target.value)}
                        />
                        <TextInputField
                            id={'postcode'}
                            label={'Postcode'}
                            placeholder={'Enter postcode'}
                            disabled={isDisabled}
                            maxLength={MAX_POSTCODE}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.postcode || ''}
                            error={errors.postcode}
                            onChange={(e) => onUpdate('postcode', e.target.value.toUpperCase())}
                        />
                    </div>

                    <div className={form.formColumn}>
                        <TextInputField
                            id="primaryPhoneNumber"
                            label="Primary Phone Number"
                            placeholder={'Enter primary phone number'}
                            disabled={isDisabled}
                            maxLength={MAX_PHONE}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.primaryPhoneNumber || ''}
                            error={errors.primaryPhoneNumber}
                            onChange={(e) => onUpdate('primaryPhoneNumber', e.target.value)}
                        />
                        <TextInputField
                            id="secondaryPhoneNumber"
                            label="Secondary Phone Number"
                            placeholder={'Enter secondary phone number'}
                            disabled={isDisabled}
                            maxLength={MAX_PHONE}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.secondaryPhoneNumber || ''}
                            error={errors.secondaryPhoneNumber}
                            onChange={(e) => onUpdate('secondaryPhoneNumber', e.target.value)}
                        />
                        <TextInputField
                            id={'emailAddress'}
                            label={'Email Address'}
                            placeholder={'Enter email address'}
                            disabled={isDisabled}
                            maxLength={MAX_EMAIL}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.emailAddress || ''}
                            error={errors.emailAddress}
                            onChange={(e) => onUpdate('emailAddress', e.target.value)}
                        />
                        <RadioButtons
                            id="preferredMethodOfContact"
                            label="Preferred Method of Contact"
                            disabled={isDisabled}
                            radioButtons={[
                                { id: 'Email', label: 'Email', onChangeVal: 'email' },
                                { id: 'Phone', label: 'Phone', onChangeVal: 'phone' },
                                { id: 'Post', label: 'Post', onChangeVal: 'post' }
                            ]}
                            value={newEntry.preferredMethodOfContact}
                            onChange={(option) => onUpdate('preferredMethodOfContact', option)}
                        />
                        <MultiSelect
                            id="communicationOptOut"
                            label="Communication Opt Out"
                            placeholder="Select communication type(s)..."
                            disabled={isDisabled}
                            menuItems={communicationOptOutTypes || []}
                            preSelectedIds={preSelectedCommunicationOptOutTypeIds || []}
                            preSelects={preSelectedCommunicationOptOutTypes}
                            onChange={(chosenIds) =>
                                onUpdate('communicationTypeOptOutIds', chosenIds)
                            }
                        />
                        <RadioButtons
                            id="careOfAddress"
                            label="Care of Address"
                            disabled={isDisabled}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.careOfAddress ?? false}
                            onChange={(option) => onUpdate('careOfAddress', option)}
                        />
                    </div>
                </div>

                <h2 className={app.sectionHeading}>Contract Details</h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <DateSelect
                            id={'startDate'}
                            label="Start Date"
                            disabled={!hasRole(qualityAndUpRoles, roles)}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.startDate}
                            onDateChange={(date) => onUpdate('startDate', date)}
                        />
                        <SingleSelect
                            id={'serviceId'}
                            label={'Service'}
                            placeholder="Select service..."
                            disabled={isDisabledUniqueId}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            mandatory={true}
                            menuItems={services}
                            selectedId={newEntry.serviceId}
                            selected={getObjFromId(services, newEntry.serviceId)}
                            error={errors.serviceId}
                            onChange={(chosenId) => onUpdate('serviceId', chosenId)}
                        />
                        <SingleSelect
                            id={'teamId'}
                            label={'Team'}
                            placeholder="Select team..."
                            disabled={isDisabledTeam}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            menuItems={teams || []}
                            selectedId={newEntry.teamId || ''}
                            selected={teams?.find((el) => el.id === newEntry.teamId) || {}}
                            error={errors.teamId}
                            onChange={(chosenId) => onUpdate('teamId', chosenId)}
                        />
                    </div>

                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'eligibilityGroupId'}
                            label={'Eligibility Group'}
                            placeholder="Select eligibility group..."
                            disabled={isDisabled}
                            mandatory={true}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            menuItems={eligibilityGroups || []}
                            selectedId={newEntry.eligibilityGroupId || ''}
                            selected={getObjFromId(eligibilityGroups, newEntry.eligibilityGroupId)}
                            error={errors.eligibilityGroupId}
                            onChange={(chosenId) => onUpdate('eligibilityGroupId', chosenId)}
                        />
                        <DateSelect
                            id="referralDate"
                            label="Referral Date"
                            disabled={isDisabledReferralDate}
                            mandatory={true}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                            value={newEntry.referralDate}
                            error={errors.referralDate}
                            onDateChange={(date) => onUpdate('referralDate', date)}
                        />
                        <SingleSelect
                            id={'dataProtectionId'}
                            label={'Data Protection'}
                            placeholder="Select data protection..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={dataProtectionDetails || []}
                            selectedId={newEntry.dataProtectionId || ''}
                            selected={getObjFromId(
                                dataProtectionDetails,
                                newEntry.dataProtectionId
                            )}
                            error={errors.dataProtectionId}
                            onChange={(chosenId) => onUpdate('dataProtectionId', chosenId)}
                        />
                    </div>
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <RadioButtons
                            id="goodNewsStory"
                            disabled={isDisabled}
                            label="Possible Good News Story?"
                            value={newEntry.goodNewsStory ?? false}
                            onChange={(option) => onUpdate('goodNewsStory', option)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        {newEntry.orionMembershipId && (
                            <div className={form.staticField}>
                                <label className={form.formLabel}>Orion ID</label>
                                <div className={form.staticText}>{newEntry.orionMembershipId}</div>
                            </div>
                        )}
                    </div>
                </div>

                {showAuditModal && (
                    <AuditEventHistoryModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        onClose={onModalClose}
                    />
                )}

                <Button
                    id="createContract"
                    content="Update Primary Details"
                    disabled={isDisabledUpdate || isButtonDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />

                {errors.businessLogicError?.error && (
                    <ScreenError error={errors.businessLogicError} clearError={clearError} />
                )}
            </form>
        </div>
    );
};

PrimaryDetails.propTypes = {
    id: PropTypes.string
};

export default PrimaryDetails;
